import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PoComboFilter, PoComboOption } from '@po-ui/ng-components';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';

interface LanguageDto {
  id: string;
  name: string;
}

type LanguageApiResponse = { items: LanguageDto[] } | LanguageDto[];

@Injectable({
  providedIn: 'root',
})
export class LanguageCustomFilterService implements PoComboFilter {
  private readonly labelCache = new Map<string, string>();

  private readonly languagesEndpoint = `${environment.applicantApiEndpoint}/applicants/languages`;

  constructor(private readonly httpClient: HttpClient) {}

  public getFilteredData(params: any, filterParams?: any): Observable<PoComboOption[]> {
    const httpParams = this.buildParams(params, filterParams);

    return this.httpClient
      .get<LanguageApiResponse>(this.languagesEndpoint, { params: httpParams })
      .pipe(
        map((response) => {
          const items = Array.isArray(response) ? response : (response?.items ?? []);

          return items.map((language) => this.toOption(language));
        }),
      );
  }

  public getObjectByValue(value: string, filterParams?: any): Observable<PoComboOption> {
    const normalizedValue =
      value !== undefined && value !== null && value !== '' ? String(value) : '';

    if (!normalizedValue) {
      return of({ value: '', label: '' });
    }

    const cachedLabel = this.labelCache.get(normalizedValue);
    if (cachedLabel) {
      return of({ value: normalizedValue, label: cachedLabel });
    }

    return this.findByFilterValue(normalizedValue, filterParams);
  }

  public cacheOption(value: string | null | undefined, label: string | null | undefined): void {
    if (!value) return;
    const normalizedValue = String(value);
    const normalizedLabel =
      label !== null && label !== undefined && String(label).trim().length > 0
        ? String(label)
        : normalizedValue;

    this.labelCache.set(normalizedValue, normalizedLabel);
  }

  public getLabel(value: string | null | undefined): string | null {
    if (!value) return null;
    return this.labelCache.get(String(value)) ?? null;
  }

  private buildParams(params: any, filterParams?: any): HttpParams {
    let httpParams = new HttpParams();

    if (params?.page !== undefined && params.page !== null) {
      httpParams = httpParams.set('page', String(params.page));
    }

    if (params?.value) {
      httpParams = httpParams.set('filter', params.value);
    }

    if (filterParams?.pageSize !== undefined && filterParams.pageSize !== null) {
      httpParams = httpParams.set('pageSize', String(filterParams.pageSize));
    }

    if (filterParams?.order) {
      httpParams = httpParams.set('order', filterParams.order);
    }

    return httpParams;
  }

  private findByFilterValue(value: string, filterParams?: any): Observable<PoComboOption> {
    const params = this.buildParams({ value, page: 1 }, { ...filterParams, pageSize: 1 })
      .set('filter', value)
      .set('pageSize', '1');

    return this.httpClient.get<LanguageApiResponse>(this.languagesEndpoint, { params }).pipe(
      map((response) => {
        const items = Array.isArray(response) ? response : (response?.items ?? []);
        const language = items[0] ?? { id: value, name: value };

        return this.toOption(language);
      }),
      catchError(() => of(this.createFallbackOption(value))),
    );
  }

  private toOption(language: LanguageDto): PoComboOption {
    const value = language.id ?? '';
    const label = language.name ?? value;

    if (value) {
      this.labelCache.set(value, label);
    }

    return { value, label };
  }

  private createFallbackOption(value: string): PoComboOption {
    const normalizedValue = String(value);
    this.labelCache.set(normalizedValue, normalizedValue);

    return { value: normalizedValue, label: normalizedValue };
  }
}
